import React from 'react';
import { FaImage, FaExternalLinkAlt, FaTrash, FaMinus, FaPlus } from 'react-icons/fa';
import formatAplicaciones from '../../../utils/formatAplicaciones';
import { validarUbicacion } from '../../../components/ubicacionValidator';
import ProductSalesMetrics from '../../../components/productos/Buscador/ProductSalesMetrics';
import { VEREDICTO_META, formatCop } from './veredictoHelpers';
import { resolveSalesRisk } from './ocSalesAlerts';
import OcStockConteoBlock from './OcStockConteoBlock';

const openProductVista = (id) => {
  if (!id) return;
  window.open(`/productos/vista/${id}`, '_blank', 'noopener,noreferrer');
};

/**
 * Extrae ficha visual desde indicadores congelados o campos top-level de la línea.
 */
export const resolveLineFicha = (line) => {
  const ind = line?.indicadores || {};
  const ficha = ind.ficha || {};
  const aplicacionesRaw = ficha.aplicaciones || ind.aplicaciones || null;
  let aplicacionesTexto = null;
  if (Array.isArray(aplicacionesRaw) && aplicacionesRaw.length) {
    aplicacionesTexto = aplicacionesRaw
      .map((a) => a.vehiculo_completo || a.label || '')
      .filter(Boolean)
      .join('; ');
  } else if (typeof aplicacionesRaw === 'string') {
    aplicacionesTexto = aplicacionesRaw;
  }

  return {
    id_producto: line.id_producto || ind.id_producto || null,
    nombre: line.nombre_producto || ind.nombre_producto || 'Sin nombre',
    sku: ficha.sku || ind.sku || null,
    codigo_barras: ind.codigo_barras || null,
    marca: ind.marca || null,
    medidas: ficha.medidas || ind.medidas || null,
    ubicacion: ind.ubicacion || null,
    aplicaciones: aplicacionesTexto,
    imagen_url: ind.imagen_url || ficha.imagen_url || null,
    stock_actual: ind.stock_actual != null ? ind.stock_actual : null,
    stock_minimo: ind.stock_minimo != null ? ind.stock_minimo : null,
  };
};

const OcLineCard = ({
  line,
  editable = false,
  onRemove,
  onChangeLine,
  onConteoUpdated,
  showInsightHint = false,
  token = null,
  canGrabarConteo = false,
}) => {
  const ficha = resolveLineFicha(line);
  const vMeta = VEREDICTO_META[line.veredicto] || VEREDICTO_META.posponer;
  const qty = Math.max(1, Math.round(Number(line.cantidad) || 1));
  const unit = Number(line.valor_unitario_estimado) || 0;
  const totalLinea = qty * unit;
  const motivo = line.motivo_override || line.demanda_latente_nota;
  const excluida = Boolean(line.excluida);
  const ind = line.indicadores || {};
  const { sinVentas, sinVentas30 } = resolveSalesRisk(ind);
  const showSales = !line.es_producto_nuevo && (
    ind.ventas_30_dias != null
    || ind.cantidad_ventas != null
    || ind.fecha_ultima_venta
  );
  const canEditQty = editable && typeof onChangeLine === 'function' && !excluida;
  const stockVal = ficha.stock_actual != null ? ficha.stock_actual : ind.stock_actual;
  const conteoOk = Boolean(
    ind.tieneMovimientosRecientes
    ?? line.tieneMovimientosRecientes,
  );

  const bumpQty = (delta) => {
    const next = Math.max(1, qty + delta);
    onChangeLine({ cantidad: next });
  };

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-start gap-3 p-3 sm:p-4 rounded-xl border transition-all group ${
        excluida
          ? 'border-red-200 bg-red-50/40 opacity-80'
          : 'border-slate-100 bg-slate-50/60 hover:border-emerald-300 hover:bg-emerald-50/20'
      }`}
    >
      <button
        type="button"
        disabled={!ficha.id_producto}
        onClick={() => openProductVista(ficha.id_producto)}
        className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg border border-slate-100 flex-shrink-0 flex items-center justify-center overflow-hidden shadow-sm self-start disabled:cursor-default"
        title={ficha.id_producto ? 'Ver ficha del producto' : 'Producto nuevo (sin ficha)'}
      >
        {ficha.imagen_url ? (
          <img
            src={ficha.imagen_url}
            alt={ficha.nombre}
            className="w-full h-full object-contain p-1"
          />
        ) : (
          <FaImage className="text-slate-300 text-xl" />
        )}
      </button>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${vMeta.badge}`}>
            {line.label || vMeta.label}
          </span>
          {line.es_producto_nuevo && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-orange-200 text-orange-900">
              Nuevo
            </span>
          )}
          {excluida && (
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-red-600 text-white">
              Quitado al aprobar
            </span>
          )}
        </div>

        {ficha.id_producto ? (
          <button
            type="button"
            onClick={() => openProductVista(ficha.id_producto)}
            className="text-left w-full"
          >
            <p
              className={`text-sm font-bold line-clamp-2 transition-colors ${
                excluida
                  ? 'text-slate-500 line-through'
                  : 'text-slate-900 group-hover:text-emerald-800'
              }`}
            >
              {ficha.nombre}
            </p>
          </button>
        ) : (
          <p
            className={`text-sm font-bold line-clamp-2 ${
              excluida ? 'text-slate-500 line-through' : 'text-slate-900'
            }`}
          >
            {ficha.nombre}
          </p>
        )}

        <p className="text-xs text-slate-600">
          {ficha.sku && (
            <>
              <span className="font-semibold">SKU:</span> {ficha.sku}
            </>
          )}
          {ficha.codigo_barras && (
            <>
              {ficha.sku && <span className="text-slate-300 mx-1">·</span>}
              <span className="font-semibold">Cód. Barras:</span> {ficha.codigo_barras}
            </>
          )}
          {!ficha.sku && !ficha.codigo_barras && line.es_producto_nuevo && (
            <span className="italic text-slate-400">Sin SKU aún</span>
          )}
        </p>

        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600">
          {ficha.marca && (
            <span><span className="font-semibold">Marca:</span> {ficha.marca}</span>
          )}
          {ficha.medidas && (
            <span><span className="font-semibold">Medidas:</span> {ficha.medidas}</span>
          )}
          {ficha.ubicacion && (
            <span><span className="font-semibold">Ubicación:</span> {validarUbicacion(ficha.ubicacion)}</span>
          )}
        </div>

        {ficha.aplicaciones && (
          <p className="text-xs text-slate-600">
            <span className="font-semibold">Aplicación:</span>{' '}
            <span className="italic">{formatAplicaciones(ficha.aplicaciones)}</span>
          </p>
        )}

        {!line.es_producto_nuevo && (
          <OcStockConteoBlock
            idProducto={ficha.id_producto}
            nombreProducto={ficha.nombre}
            stockActual={stockVal}
            stockMinimo={ficha.stock_minimo ?? ind.stock_minimo}
            diasInventario={ind.dias_inventario ?? line.dias_inventario}
            tieneMovimientosRecientes={conteoOk}
            nombreEmpleado={ind.nombre_empleado || line.nombre_empleado}
            fechaUltimoMovimiento={ind.fecha_ultimo_movimiento || line.fecha_ultimo_movimiento}
            token={token}
            canGrabar={canGrabarConteo}
            tooltipId={`oc-line-conteo-${ficha.id_producto || 'x'}`}
            onUpdated={(result) => {
              if (onConteoUpdated) onConteoUpdated(result);
              else if (onChangeLine) {
                onChangeLine({
                  indicadores: {
                    ...ind,
                    stock_actual: result.stock_actual,
                    tieneMovimientosRecientes: result.tieneMovimientosRecientes,
                    nombre_empleado: result.nombre_empleado,
                    fecha_ultimo_movimiento: result.fecha_ultimo_movimiento,
                  },
                });
              }
            }}
          />
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {!canEditQty && (
            <span className={`text-sm font-bold ${excluida ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>
              <span className="text-slate-600 font-semibold text-xs">Pedido: </span>
              {qty} × {formatCop(unit)}
              {' + IVA = '}
              {formatCop(totalLinea)}
            </span>
          )}
          {line.cantidad_solicitada != null
            && Math.abs(Number(line.cantidad) - Number(line.cantidad_solicitada)) > 0.0005 && (
            <span className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-0.5">
              Pedido original: {line.cantidad_solicitada}
            </span>
          )}
        </div>

        {canEditQty && (
          <div className="grid sm:grid-cols-2 gap-3 pt-1">
            <div className="text-sm">
              <span className="font-semibold text-slate-700">Cantidad</span>
              <div className="mt-1.5 flex items-stretch gap-2">
                <button
                  type="button"
                  aria-label="Bajar cantidad"
                  onClick={() => bumpQty(-1)}
                  className="h-11 w-11 shrink-0 rounded-xl border border-slate-300 bg-white text-slate-800 flex items-center justify-center font-bold hover:bg-slate-50"
                >
                  <FaMinus />
                </button>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  className="h-11 min-w-0 flex-1 rounded-xl border border-slate-300 px-3 text-center text-base font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                  value={qty}
                  onChange={(e) => {
                    const n = Math.max(1, Math.round(Number(e.target.value) || 1));
                    onChangeLine({ cantidad: n });
                  }}
                />
                <button
                  type="button"
                  aria-label="Subir cantidad"
                  onClick={() => bumpQty(1)}
                  className="h-11 w-11 shrink-0 rounded-xl border border-slate-300 bg-white text-slate-800 flex items-center justify-center font-bold hover:bg-slate-50"
                >
                  <FaPlus />
                </button>
              </div>
            </div>
            <div className="text-sm">
              <span className="font-semibold text-slate-700">Valor unitario + IVA</span>
              <div className="mt-1.5 flex h-11 items-stretch overflow-hidden rounded-xl border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-emerald-300">
                <span className="flex items-center px-3 text-base font-bold text-slate-500 bg-slate-50 border-r border-slate-200">
                  $
                </span>
                <input
                  type="number"
                  min={0}
                  inputMode="decimal"
                  className="min-w-0 flex-1 border-0 px-3 text-base font-semibold text-slate-900 focus:outline-none focus:ring-0"
                  value={unit}
                  onChange={(e) => {
                    const n = Math.max(0, Number(e.target.value) || 0);
                    onChangeLine({ valor_unitario_estimado: n });
                  }}
                />
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Total línea ≈ {formatCop(totalLinea)}
              </p>
            </div>
          </div>
        )}

        {showSales && (
          <ProductSalesMetrics
            compact
            producto={{
              ventas_30_dias: ind.ventas_30_dias,
              cantidad_ventas: ind.cantidad_ventas,
              fecha_ultima_venta: ind.fecha_ultima_venta,
            }}
          />
        )}

        {sinVentas && !line.es_producto_nuevo && (
          <p className="text-xs font-bold text-red-900 bg-red-50 border border-red-300 rounded-lg px-2 py-1.5 mt-1">
            Sin ventas registradas — pedir es riesgoso.
          </p>
        )}

        {!sinVentas && sinVentas30 && !line.es_producto_nuevo && (
          <p className="text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mt-1">
            Sin ventas en 30 días.
          </p>
        )}

        {motivo && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1 mt-1">
            <span className="font-semibold">Motivo:</span> {motivo}
          </p>
        )}

        {showInsightHint && line.indicadores?.sugerencia_inventario && (
          <p className="text-[11px] text-slate-500 mt-0.5">{line.indicadores.sugerencia_inventario}</p>
        )}
      </div>

      <div className="flex sm:flex-col gap-2 shrink-0 self-start sm:self-center">
        {ficha.id_producto && (
          <button
            type="button"
            onClick={() => openProductVista(ficha.id_producto)}
            className="bg-white hover:bg-emerald-600 hover:text-white p-2.5 rounded-xl border border-slate-200 shadow-sm transition-all flex items-center justify-center"
            title="Ver ficha"
          >
            <FaExternalLinkAlt className="text-sm" />
          </button>
        )}
        {editable && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="bg-white hover:bg-red-600 hover:text-white p-2.5 rounded-xl border border-slate-200 shadow-sm transition-all flex items-center justify-center text-red-600"
            title="Quitar de la OC"
          >
            <FaTrash className="text-sm" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OcLineCard;
